import React, { useCallback, useRef, useState, memo } from 'react';
import toTitleCase from '@hooks/toTitleCase';
import apis from '@api/api';
import { HANDLE_FAVOURITE } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Pressable, View, Platform, Dimensions, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import globalStyles from '@stylesheet';
import { useDispatch, useSelector } from 'react-redux';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { useDeviceType } from '@hooks/useDeviceType';
import { useNavigation } from '@react-navigation/native';
import LoadingIndicator from '@components/Global/Spinner';
import { Text as RNText } from 'react-native';
import getDaysAgo from '@utils/getDaysAgo';

export interface RenderProjectProps {
  item: any;
  index: number;
}

const MeetingItem = memo(({ item, index }: RenderProjectProps) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const navigation = useNavigation();

  const [context, setContext] = useState(false);
  const [playing, setPlaying] = useState(false);

  const _handleFavourite = useCallback(async (id) => {
    dispatch({ type: HANDLE_FAVOURITE, payload: { id, type: 'projects' } })
    await apis.addFavourite(id, 'projects', {});
  }, [dispatch]);

  const _handleModalState = useCallback((item) => {
    // Handle modal state logic
    console.log('Modal state for item:', item._id);
  }, []);

  const formatDuration = useCallback((duration) => {
    if (!duration) return '00:00';
    try {
      return new Date(duration).toISOString().substr(14, 5);
    } catch {
      return '00:00';
    }
  }, []);

  return (
    <View style={{ overflow: 'hidden', flex: 1 }}>
      <TouchableOpacity 
        activeOpacity={0.9}
        onLongPress={() => setContext(!context)} 
        onPress={() => {
          (navigation as any).navigate('Calendar', { 
            screen: 'MeetingPage', 
            params: { 
              _id: item._id,
              title: item.title,
              createdAt: item.createdAt,
              createdBy: item.createdBy,
              summary: item.summary,
              transcript: item.transcript
            } 
          });
        }}
      >
        <View style={[
          globalStyles.modernCard,
          theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
          { marginVertical: 8 }
        ]}>
          <View style={{ gap: 16, padding: 16 }}>
            {/* Header Section */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}>
              <View style={{ flex: 1, gap: 8 }}>
                <RNText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 18,
                  fontWeight: '700',
                  lineHeight: 24
                }}>
                  {item.title || "Untitled Meeting"}
                </RNText>
                
                <View style={{ flexDirection: 'row', gap: 16 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <MaterialIcons 
                      name="schedule" 
                      size={16} 
                      color={theme ? '#64748b' : '#94a3b8'} 
                    />
                    <RNText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {formatDuration(item.duration)}
                    </RNText>
                  </View>
                  
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <MaterialIcons 
                      name="event" 
                      size={16} 
                      color={theme ? '#64748b' : '#94a3b8'} 
                    />
                    <RNText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {getDaysAgo(item.createdAt)}
                    </RNText>
                  </View>
                </View>
              </View>
              
              <View style={{ marginLeft: 16 }}>
                <AvatarHandler 
                  avatarImageStyle={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: 12 
                  }} 
                  replacementImageStyle={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: 12 
                  }} 
                  avatar={item.createdBy?.profile_information?.avatar?.img} 
                />
              </View>
            </View>

            {/* Content Section */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <TouchableOpacity 
                style={[
                  globalStyles.modernActionButton,
                  theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark
                ]}
                onPress={() => setPlaying(!playing)}
              >
                <MaterialIcons 
                  name={playing ? "pause" : "play-arrow"} 
                  size={24} 
                  color="white" 
                />
              </TouchableOpacity>
              
              <View style={[
                globalStyles.modernCardSmall,
                theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle,
                { flex: 1 }
              ]}>
                <RNText numberOfLines={5} style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14,
                  lineHeight: 20,
                  ellipsizeMode: 'tail',
                  padding: 12
                }}>
                  {item.summary || item.description || "Meeting summary will appear here..."}
                </RNText>
              </View>
              
              {item.status === 'pending' && (
                <View style={{ 
                  width: 44, 
                  height: 44, 
                  justifyContent: 'center', 
                  alignItems: 'center' 
                }}>
                  <LoadingIndicator 
                    color={theme ? '#64748b' : '#94a3b8'} 
                    type="small" 
                  />
                </View>
              )}
            </View>

            {/* Action Button */}
            <TouchableOpacity 
              style={[
                globalStyles.modernActionButton,
                theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark,
                {
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingHorizontal: 20,
                  paddingVertical: 14,
                  borderRadius: 12,
                  minHeight: 50,
                  width: '100%'
                }
              ]}
              onPress={() => {
                (navigation as any).navigate('Calendar', { 
                  screen: 'MeetingPage', 
                  params: { 
                    _id: item._id,
                    title: item.title,
                    createdAt: item.createdAt,
                    createdBy: item.createdBy,
                    summary: item.summary,
                    transcript: item.transcript
                  } 
                });
              }}
            >
              <RNText style={{ 
                color: 'white', 
                fontWeight: '700', 
                fontSize: 16,
                marginRight: 10
              }}>
                Open Meeting
              </RNText>
              <MaterialIcons name="arrow-forward" size={20} color="white" />
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
});

export default MeetingItem;
